import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, GraduationCap, Edit, Trash2 } from "lucide-react";

const Assessments = ({ user }) => {
  const [assessments, setAssessments] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedTerm, setSelectedTerm] = useState("all");
  const [selectedSubject, setSelectedSubject] = useState("all");
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentAssessments, setStudentAssessments] = useState([]);
  const [editingAssessment, setEditingAssessment] = useState(null);

  const [formData, setFormData] = useState({
    student_id: "",
    class_id: "",
    subject: "",
    term: "term1",
    marks: "",
    max_marks: "100",
    date: new Date().toISOString().split("T")[0],
  });

  const [editForm, setEditForm] = useState({
    subject: "",
    term: "term1",
    marks: "",
    max_marks: "100",
    date: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      fetchAssessments();
    }
  }, [selectedClass]);

  const fetchData = async () => {
    try {
      const [studentsRes, classesRes, subjectsRes] = await Promise.all([
        axios.get(`${API}/students`),
        axios.get(`${API}/classes`),
        axios.get(`${API}/subjects`),
      ]);

      const allClasses = classesRes.data;
      // Backend already filters classes based on user role - no additional filtering needed

      setStudents(studentsRes.data.filter(s => s.id));
      setClasses(allClasses.filter(c => c.id));
      setSubjectOptions((subjectsRes.data || []).filter(s => s.name));
      if (allClasses.length > 0) {
        setSelectedClass(allClasses[0].id);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssessments = async () => {
    try {
      const response = await axios.get(
        `${API}/assessments?class_id=${selectedClass}`
      );
      setAssessments(response.data);
    } catch (error) {
      toast.error("Failed to load assessments");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.student_id || !formData.subject?.trim()) {
      toast.error("Please select a student and a subject");
      return;
    }
    try {
      const submitData = {
        ...formData,
        marks: parseFloat(formData.marks),
        max_marks: parseFloat(formData.max_marks),
      };
      console.log("Submitting assessment data:", submitData);
      await axios.post(`${API}/assessments`, submitData);
      toast.success("✅ Assessment added successfully");
      fetchAssessments();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Assessment creation error:", error.response?.data || error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to add assessment";
      toast.error(errorMessage);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.subject?.trim()) {
      toast.error("Please select a subject");
      return;
    }
    try {
      await axios.put(`${API}/assessments/${editingAssessment.id}`, {
        ...editForm,
        marks: parseFloat(editForm.marks),
        max_marks: parseFloat(editForm.max_marks),
      });
      toast.success("✅ Assessment updated successfully");
      setIsEditDialogOpen(false);
      fetchStudentAssessments(selectedStudent);
      fetchAssessments();
    } catch (error) {
      toast.error("Failed to update assessment");
    }
  };

  const handleDelete = async (assessmentId) => {
    if (!window.confirm("Are you sure you want to delete this assessment?")) {
      return;
    }
    try {
      await axios.delete(`${API}/assessments/${assessmentId}`);
      toast.success("❌ Assessment deleted");
      fetchStudentAssessments(selectedStudent);
      fetchAssessments();
    } catch (error) {
      toast.error("Failed to delete assessment");
    }
  };

  const resetForm = () => {
    setFormData({
      student_id: "",
      class_id: "",
      subject: "",
      term: "term1",
      marks: "",
      max_marks: "100",
      date: new Date().toISOString().split("T")[0],
    });
  };

  const openEditDialog = (assessment) => {
    setEditingAssessment(assessment);
    setEditForm({
      subject: assessment.subject || "",
      term: assessment.term,
      marks: String(assessment.marks ?? ""),
      max_marks: String(assessment.max_marks ?? "100"),
      date: assessment.date,
    });
    setIsEditDialogOpen(true);
  };

  const fetchStudentAssessments = async (student) => {
    try {
      const response = await axios.get(
        `${API}/assessments?student_id=${student.id}`
      );
      setStudentAssessments(response.data);
    } catch (error) {
      toast.error("Failed to load student assessments");
    }
  };

  const openStudentDetail = (student) => {
    setSelectedStudent(student);
    fetchStudentAssessments(student);
    setIsDetailDialogOpen(true);
  };

  const calculateAverage = (assessmentsList) => {
    if (assessmentsList.length === 0) return 0;
    const total = assessmentsList.reduce((sum, a) => sum + (a.marks / a.max_marks) * 100, 0);
    return (total / assessmentsList.length).toFixed(1);
  };

  const getGrade = (percentage) => {
    if (percentage >= 90) return { letter: "A", color: "bg-green-100 text-green-700" };
    if (percentage >= 80) return { letter: "B", color: "bg-blue-100 text-blue-700" };
    if (percentage >= 70) return { letter: "C", color: "bg-yellow-100 text-yellow-700" };
    if (percentage >= 60) return { letter: "D", color: "bg-orange-100 text-orange-700" };
    return { letter: "F", color: "bg-red-100 text-red-700" };
  };

  const filteredStudents = students.filter(s => {
    if (selectedClass && s.class_id !== selectedClass) return false;
    
    const studentAssessmentsList = assessments.filter(a => a.student_id === s.id);
    
    if (selectedTerm !== "all") {
      const hasTermAssessment = studentAssessmentsList.some(a => a.term === selectedTerm);
      if (!hasTermAssessment) return false;
    }
    
    if (selectedSubject !== "all") {
      const hasSubjectAssessment = studentAssessmentsList.some(a => a.subject === selectedSubject);
      if (!hasSubjectAssessment) return false;
    }
    
    return true;
  });

  const subjects = [...new Set(assessments.map(a => a.subject))].filter(s => s && s.trim());

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="assessments-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-[#1E624A]">Assessments</h2>
          <p className="text-sm text-gray-600">Imtixaannada</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsDialogOpen(true);
          }}
          className="bg-[#1E624A] hover:bg-[#164935]"
          data-testid="add-assessment-btn"
        >
          <Plus size={20} className="mr-2" />
          Add Assessment
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-md">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-sm text-gray-700 mb-2">Class</Label>
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {classes.map((cls) => (
                  <SelectItem key={cls.id} value={cls.id}>
                    {cls.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm text-gray-700 mb-2">Term</Label>
            <Select value={selectedTerm} onValueChange={setSelectedTerm}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Terms</SelectItem>
                <SelectItem value="month1">Month 1</SelectItem>
                <SelectItem value="month2">Month 2</SelectItem>
                <SelectItem value="month3">Month 3</SelectItem>
                <SelectItem value="month4">Month 4</SelectItem>
                <SelectItem value="term1">Term 1</SelectItem>
                <SelectItem value="term2">Term 2</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm text-gray-700 mb-2">Subject</Label>
            <Select value={selectedSubject} onValueChange={setSelectedSubject}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subjects</SelectItem>
                {subjects.map((subject) => (
                  <SelectItem key={subject} value={subject}>
                    {subject}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Student Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredStudents.map((student) => {
          const studentAssessmentsList = assessments.filter(a => a.student_id === student.id);
          const average = calculateAverage(studentAssessmentsList);
          const grade = getGrade(parseFloat(average));

          return (
            <div
              key={student.id}
              onClick={() => openStudentDetail(student)}
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all cursor-pointer group"
              data-testid={`student-assessment-card-${student.id}`}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-[#E4C987] rounded-lg flex items-center justify-center flex-shrink-0">
                  <GraduationCap size={24} className="text-[#1E624A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 truncate group-hover:text-[#1E624A] transition-colors">
                    {student.name}
                  </h3>
                  <p className="text-sm text-gray-600">Class: {classes.find(c => c.id === student.class_id)?.name || "N/A"}</p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Assessments:</span>
                  <span className="font-semibold text-gray-900">{studentAssessmentsList.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Average:</span>
                  <span className="font-semibold text-gray-900">{average}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Grade:</span>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${grade.color}`}>
                    {grade.letter}
                  </span>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t">
                <div className="text-xs text-gray-500 italic">Click to view details</div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredStudents.length === 0 && (
        <div className="bg-white rounded-xl p-12 text-center text-gray-500">
          No students found for the selected filters
        </div>
      )}

      {/* Add Assessment Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="text-xl text-[#1E624A]">Add New Assessment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="overflow-y-auto px-6 py-4 space-y-4 flex-1">
              <div className="space-y-2">
                <Label htmlFor="student_id">Student *</Label>
                <Select
                  value={formData.student_id}
                  onValueChange={(value) => {
                    const selectedStudent = students.find(s => s.id === value);
                    setFormData({ 
                      ...formData, 
                      student_id: value,
                      class_id: selectedStudent ? selectedStudent.class_id : ""
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select student" />
                  </SelectTrigger>
                  <SelectContent>
                    {students.filter(s => s.class_id === selectedClass).map((student) => (
                      <SelectItem key={student.id} value={student.id}>
                        {student.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject *</Label>
                  <Select
                    value={formData.subject}
                    onValueChange={(value) => setFormData({ ...formData, subject: value })}
                  >
                    <SelectTrigger id="subject" data-testid="assessment-subject-select">
                      <SelectValue placeholder={subjectOptions.length === 0 ? "No subjects — ask admin to add" : "Select subject"} />
                    </SelectTrigger>
                    <SelectContent>
                      {subjectOptions.map((s) => (
                        <SelectItem key={s.id} value={s.name}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="term">Term *</Label>
                  <Select
                    value={formData.term}
                    onValueChange={(value) => setFormData({ ...formData, term: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="month1">Month 1</SelectItem>
                      <SelectItem value="month2">Month 2</SelectItem>
                      <SelectItem value="month3">Month 3</SelectItem>
                      <SelectItem value="month4">Month 4</SelectItem>
                      <SelectItem value="term1">Term 1</SelectItem>
                      <SelectItem value="term2">Term 2</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="marks">Marks *</Label>
                  <Input
                    id="marks"
                    type="number"
                    step="0.1"
                    value={formData.marks}
                    onChange={(e) => setFormData({ ...formData, marks: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max_marks">Max Marks *</Label>
                  <Input
                    id="max_marks"
                    type="number"
                    step="0.1"
                    value={formData.max_marks}
                    onChange={(e) => setFormData({ ...formData, max_marks: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="border-t bg-gray-50 px-6 py-4 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#1E624A] hover:bg-[#164935]">
                Add Assessment
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Student Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-[#1E624A] to-[#2a8a6a]">
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              🎓 Assessment Details — {selectedStudent?.name}
            </DialogTitle>
            <div className="flex gap-6 mt-3 text-sm text-white/90">
              <div>
                <p className="text-xs text-white/70">Total Assessments</p>
                <p className="font-bold text-lg">{studentAssessments.length}</p>
              </div>
              <div>
                <p className="text-xs text-white/70">Average</p>
                <p className="font-bold text-lg">{calculateAverage(studentAssessments)}%</p>
              </div>
              <div>
                <p className="text-xs text-white/70">Grade</p>
                <p className="font-bold text-lg">{getGrade(parseFloat(calculateAverage(studentAssessments))).letter}</p>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-6">
            {studentAssessments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Subject</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">Term</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">Score</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">Percentage</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">Grade</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Date</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {studentAssessments.map((assessment) => {
                      const percentage = (assessment.marks / assessment.max_marks) * 100;
                      const grade = getGrade(percentage);
                      return (
                        <tr key={assessment.id} className="hover:bg-gray-50">
                          <td className="px-3 py-3 text-sm font-medium text-gray-900">
                            {assessment.subject || <span className="text-red-600 italic">No subject — edit to fix</span>}
                          </td>
                          <td className="px-3 py-3 text-center text-sm text-gray-700">
                            {assessment.term === "month1" ? "Month 1" : 
                             assessment.term === "month2" ? "Month 2" :
                             assessment.term === "month3" ? "Month 3" :
                             assessment.term === "month4" ? "Month 4" :
                             assessment.term === "term1" ? "Term 1" : 
                             assessment.term === "term2" ? "Term 2" : "Unknown"}
                          </td>
                          <td className="px-3 py-3 text-center text-sm text-gray-900">
                            {assessment.marks} / {assessment.max_marks}
                          </td>
                          <td className="px-3 py-3 text-center text-sm font-semibold text-gray-900">
                            {percentage.toFixed(1)}%
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full font-bold ${grade.color}`}>
                              {grade.letter}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-700">
                            {assessment.date}
                          </td>
                          <td className="px-3 py-3 text-center">
                            <div className="flex gap-2 justify-center">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditDialog(assessment)}
                                className="h-8 w-8 p-0"
                              >
                                <Edit size={14} />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDelete(assessment.id)}
                                className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <GraduationCap size={48} className="mx-auto mb-3 text-gray-300" />
                <p className="text-lg font-medium">No assessments found</p>
                <p className="text-sm mt-1">Add assessments to track this student&apos;s performance</p>
              </div>
            )}
          </div>

          <div className="border-t bg-gray-50 px-6 py-4 flex justify-end">
            <Button variant="outline" onClick={() => setIsDetailDialogOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Assessment Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="text-xl text-[#1E624A]">Edit Assessment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="overflow-y-auto px-6 py-4 space-y-4 flex-1">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_subject">Subject *</Label>
                  <Select
                    value={editForm.subject}
                    onValueChange={(value) => setEditForm({ ...editForm, subject: value })}
                  >
                    <SelectTrigger id="edit_subject" data-testid="edit-assessment-subject-select">
                      <SelectValue placeholder={subjectOptions.length === 0 ? "No subjects — ask admin to add" : "Select subject"} />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Include the current value even if it's not in the shared list (legacy free-text values) */}
                      {editForm.subject && !subjectOptions.some((s) => s.name === editForm.subject) && (
                        <SelectItem value={editForm.subject}>{editForm.subject} (legacy)</SelectItem>
                      )}
                      {subjectOptions.map((s) => (
                        <SelectItem key={s.id} value={s.name}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_term">Term *</Label>
                  <Select
                    value={editForm.term}
                    onValueChange={(value) => setEditForm({ ...editForm, term: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="month1">Month 1</SelectItem>
                      <SelectItem value="month2">Month 2</SelectItem>
                      <SelectItem value="month3">Month 3</SelectItem>
                      <SelectItem value="month4">Month 4</SelectItem>
                      <SelectItem value="term1">Term 1</SelectItem>
                      <SelectItem value="term2">Term 2</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_marks">Marks *</Label>
                  <Input
                    id="edit_marks"
                    type="number"
                    step="0.1"
                    value={editForm.marks}
                    onChange={(e) => setEditForm({ ...editForm, marks: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_max_marks">Max Marks *</Label>
                  <Input
                    id="edit_max_marks"
                    type="number"
                    step="0.1"
                    value={editForm.max_marks}
                    onChange={(e) => setEditForm({ ...editForm, max_marks: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_date">Date *</Label>
                <Input
                  id="edit_date"
                  type="date"
                  value={editForm.date}
                  onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="border-t bg-gray-50 px-6 py-4 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#1E624A] hover:bg-[#164935]">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Assessments;
